import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get Organization Roles
 * Retrieve a list of all roles in the organization accessible to the authenticated user.
 * @returns {Promise<{status: string, data?: array, error?: string, message?: string}>}
 */
export async function getOrgRoles() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/role/get`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching organization roles:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Roles and Permissions
 * Retrieve roles with their associated permissions filtered by role scope.
 * @param {string} roleScope - Role scope filter (e.g., ORGANIZATION, PROJECT)
 * @returns {Promise<{status: string, data?: object, error?: string, message?: string}>}
 */
export async function getRoleAndPermission(roleScope) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/role/getRoleAndPermission`,
      {
        params: { roleScope },
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching roles and permissions:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Role Details
 * Retrieve detailed information about a specific role including its permissions.
 * @param {string} scope - Role scope (e.g., ORGANIZATION, PROJECT)
 * @param {string} roleId - UUID of the role
 * @returns {Promise<{status: string, data?: object, error?: string, message?: string}>}
 */
export async function getRoleDetails(scope, roleId) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/role/getRoleDetails`,
      {
        params: { scope, roleId },
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching role details:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create Role with Permissions
 * Create a new role with associated permissions.
 * @param {object} params - Request parameters
 * @param {object} params.formData - Role form data (name, description, type)
 * @param {array} params.permissions - Array of permission objects with id
 * @returns {Promise<{status: string, data?: object, error?: string, message?: string}>}
 */
export async function createRoleWithPermissions({ formData, permissions = [] } = {}) {
  try {
    if (!formData || !formData.name || !formData.type) {
      return {
        status: STATUS.ERROR,
        error: "Missing required fields in Role Detail Data",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/role/create`,
      { formData, permissions },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message || "Role created successfully",
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating role:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update Role with Permissions
 * Update an existing role and its associated permissions.
 * @param {string} id - UUID of the role to update
 * @param {object} params - Request parameters
 * @param {object} params.formData - Role form data (name, description, type)
 * @param {array} params.permissions - Array of permission objects with id
 * @returns {Promise<{status: string, data?: object, error?: string, message?: string}>}
 */
export async function updateRoleWithPermissions(id, { formData, permissions = [] } = {}) {
  try {

    if (!formData || !formData.name || !formData.type) {
      return {
        status: STATUS.ERROR,
        error: "Missing required fields in Role Detail Data.",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/role/update/${id}`,
      { formData, permissions },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message || "Role updated successfully",
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating role:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Roles for Project
 * Retrieve roles filtered by role scope for project assignment.
 * @param {string} roleScope - Role scope filter (e.g., ORGANIZATION, PROJECT)
 * @param {string} projectId - Optional project ID (defaults to current project)
 * @returns {Promise<{status: string, data?: array, error?: string, message?: string}>}
 */
export async function getRolesForProject(roleScope, projectId = null) {
  try {
    const projId = projectId || getProjectId();
    
    if (!projId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projId}/role/getRolesForProject`,
      {
        params: { roleScope },
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching roles for project:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete Role(s) with Permissions
 * Delete one or more roles and their associated permissions.
 * @param {array} ids - Array of role IDs to delete (minimum 1)
 * @returns {Promise<{status: string, data?: object, error?: string, message?: string}>}
 */
export async function deleteRolesWithPermissions(ids) {
  try {
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Role IDs must be provided as a non-empty array.",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/role/delete`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.status === 'success' || response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message || "Role(s) deleted successfully",
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting roles:", error);
    return extractErrorInfo(error);
  }
}

